import { useState } from 'react';
import { useSeoMeta } from '@unhead/react';
import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { UploadModal } from '@/components/UploadModal';
import { VideoCard, VideoCardSkeleton } from '@/components/VideoCard';
import { isValidVideoEvent, deduplicateVideos } from '@/hooks/useVideoFeed';
import type { VideoEvent } from '@/hooks/useVideoFeed';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { LoginArea } from '@/components/auth/LoginArea';

function useFollowingVideos(pubkey: string | undefined) {
  const { nostr } = useNostr();

  const followsQuery = useQuery({
    queryKey: ['follows', pubkey],
    queryFn: async (ctx) => {
      if (!pubkey) return [];
      const [followEvent] = await nostr.query(
        [{ kinds: [3], authors: [pubkey], limit: 1 }],
        { signal: ctx.signal },
      );
      if (!followEvent) return [];
      return followEvent.tags.filter(([t]) => t === 'p').map(([, pk]) => pk);
    },
    enabled: !!pubkey,
    staleTime: 60000,
  });

  const follows = followsQuery.data ?? [];

  const videosQuery = useQuery({
    queryKey: ['following-videos', follows.join(',')],
    queryFn: async (ctx) => {
      if (!follows.length) return [];
      const events = await nostr.query(
        [{ kinds: [21, 22, 34235, 34236], authors: follows.slice(0, 200), limit: 40 }],
        { signal: ctx.signal },
      );
      return deduplicateVideos(events.filter(isValidVideoEvent)).sort((a, b) => b.created_at - a.created_at) as VideoEvent[];
    },
    enabled: follows.length > 0,
    staleTime: 60000,
  });

  return { ...videosQuery, followsLoading: followsQuery.isLoading, followsCount: follows.length };
}

export default function FollowingPage() {
  const [uploadOpen, setUploadOpen] = useState(false);
  const { user } = useCurrentUser();
  const { data: videos, isLoading, followsLoading, followsCount } = useFollowingVideos(user?.pubkey);

  useSeoMeta({
    title: 'Following – Goblinbox',
    description: 'Videos from people you follow on Nostr.',
  });

  return (
    <Layout onUploadClick={() => setUploadOpen(true)}>
      <div className="px-4 sm:px-6 py-6" style={{ maxWidth: 1400, margin: '0 auto' }}>
        <div className="mb-6">
          <h1 className="flex items-center gap-2 text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" style={{ color: 'var(--gb-accent)' }}>
              <path d="M20 8H4V6h16v2zm-2-6H6v2h12V2zm4 10v8c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2v-8c0-1.1.9-2 2-2h16c1.1 0 2 .9 2 2zm-6 4l-6-3.27v6.53L16 16z" />
            </svg>
            Following
          </h1>
          {user && followsCount > 0 && (
            <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
              Videos from {followsCount} account{followsCount !== 1 ? 's' : ''} you follow
            </p>
          )}
        </div>

        {!user ? (
          <div
            className="rounded-xl py-16 px-8 text-center max-w-md mx-auto"
            style={{ border: '2px dashed var(--gb-border)' }}
          >
            <div className="text-5xl mb-4">👋</div>
            <h2 className="text-lg font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>Sign in to see videos</h2>
            <p className="text-sm mb-6" style={{ color: 'var(--text-secondary)' }}>
              Connect your Nostr account to see videos from people you follow.
            </p>
            <LoginArea className="max-w-48 mx-auto" />
          </div>
        ) : followsLoading || isLoading ? (
          <div className="video-grid">
            {Array.from({ length: 8 }).map((_, i) => <VideoCardSkeleton key={i} />)}
          </div>
        ) : videos && videos.length > 0 ? (
          <div className="video-grid">
            {videos.map(event => <VideoCard key={event.id} event={event} />)}
          </div>
        ) : followsCount === 0 ? (
          <div className="rounded-xl py-16 px-8 text-center" style={{ border: '2px dashed var(--gb-border)' }}>
            <div className="text-5xl mb-4">🫂</div>
            <h2 className="text-lg font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>Not following anyone yet</h2>
            <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>
              Follow other Nostr users to see their videos here.
            </p>
            <Link
              to="/"
              className="inline-block px-4 py-2 rounded font-medium text-sm text-white hover:opacity-90 transition-all no-underline"
              style={{ background: 'var(--gb-accent)' }}
            >
              Browse all videos
            </Link>
          </div>
        ) : (
          <div className="rounded-xl py-16 px-8 text-center" style={{ border: '2px dashed var(--gb-border)' }}>
            <div className="text-5xl mb-4">📹</div>
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
              The people you follow haven't posted any videos yet. Check back later!
            </p>
          </div>
        )}
      </div>
      <UploadModal open={uploadOpen} onClose={() => setUploadOpen(false)} />
    </Layout>
  );
}
